import { useEffect, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { htmlLineBreaksToNewlines } from '@realadvisor/agency-utils';
import { Pencil, Reply, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { RaTextField } from '@modules/_shared/components/form/RaTextfield';
import { MutationErrorModal } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import { REVIEW_REPLY_ANCHORS } from '@modules/reviews/reviewReplyTour';
import { UPDATE_REVIEW_ANSWER } from '@modules/reviews/reviewsQueries';
import { CardButton } from '@modules/visibility-team/MemberDetailsCard';
import { useLocale } from '@shared/hooks/locale';

type AnswerFormValues = { answer: string };

const MAX_ANSWER_LENGTH = 1000;

type ReviewAnswerCardProps = {
  reviewId: string;
  ownerAnswer: string | null;
  ownerAnswerDate: string | null;
  canEdit: boolean;
  isOwnReply: boolean;
};

export const ReviewAnswerCard = ({
  reviewId,
  ownerAnswer,
  ownerAnswerDate,
  canEdit,
  isOwnReply,
}: ReviewAnswerCardProps) => {
  const { t, dateLocale } = useLocale();
  const [isEditing, setIsEditing] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [mutationError, setMutationError] = useState<ErrorLike | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<AnswerFormValues>({
    defaultValues: { answer: ownerAnswer ?? '' },
  });

  const draft = watch('answer') ?? '';

  useEffect(() => {
    reset({ answer: ownerAnswer ?? '' });
    setIsEditing(false);
    setDeleteConfirmOpen(false);
  }, [reviewId, ownerAnswer, reset]);

  const [updateAnswer, { loading: saving }] = useMutation(UPDATE_REVIEW_ANSWER);

  const hasAnswer = ownerAnswer != null && ownerAnswer.trim() !== '';

  const formattedDate =
    ownerAnswerDate != null
      ? new Intl.DateTimeFormat(dateLocale.code, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }).format(new Date(ownerAnswerDate))
      : null;

  const startEditing = () => {
    reset({ answer: ownerAnswer ?? '' });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    reset({ answer: ownerAnswer ?? '' });
    setIsEditing(false);
  };

  const submit = handleSubmit(({ answer }) => {
    const trimmed = answer.trim();
    if (trimmed === '') {
      return;
    }

    updateAnswer({
      variables: { id: reviewId, answer: trimmed },
      onCompleted: () => {
        setIsEditing(false);
      },
      onError: setMutationError,
    });
  });

  const handleDelete = () => {
    updateAnswer({
      variables: { id: reviewId, answer: null },
      onCompleted: () => {
        setDeleteConfirmOpen(false);
        setIsEditing(false);
        reset({ answer: '' });
      },
      onError: err => {
        setDeleteConfirmOpen(false);
        setMutationError(err);
      },
    });
  };

  const readOnly = !canEdit;

  if (!hasAnswer && readOnly) {
    return null;
  }

  const editorView = (
    <form onSubmit={submit}>
      <Stack spacing={1}>
        <RaTextField
          name="answer"
          register={register}
          errors={errors}
          required
          multiline
          minRows={4}
          placeholder={t('Write your reply…')}
          disabled={saving}
          inputProps={{ maxLength: MAX_ANSWER_LENGTH }}
        />
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('{{count}} / {{max}} characters', {
              count: draft.length,
              max: MAX_ANSWER_LENGTH,
            })}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={cancelEditing} disabled={saving}>
              {t('Cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              type="submit"
              disabled={saving || draft.trim() === ''}
              startIcon={
                saving ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {t('Save')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </form>
  );

  const readView = (
    <Stack spacing={1}>
      <Typography
        variant="body1"
        sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
      >
        {ownerAnswer && htmlLineBreaksToNewlines(ownerAnswer)}
      </Typography>
      {formattedDate != null && (
        <Typography variant="caption" color="text.secondary">
          {t('Replied on {{date}}', { date: formattedDate })}
        </Typography>
      )}
    </Stack>
  );

  const action =
    !isEditing && hasAnswer && canEdit ? (
      <Stack direction="row" {...tourAnchor(REVIEW_REPLY_ANCHORS.manage)}>
        <IconButton
          size="small"
          onClick={startEditing}
          aria-label={t('Edit reply')}
        >
          <Pencil size={16} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setDeleteConfirmOpen(true)}
          aria-label={t('Delete reply')}
        >
          <Trash2 size={16} />
        </IconButton>
      </Stack>
    ) : null;

  if (!hasAnswer && !isEditing) {
    return (
      <Box sx={{ m: 2 }}>
        <CardButton
          {...tourAnchor(REVIEW_REPLY_ANCHORS.reply)}
          variant="contained"
          endIcon={<Reply size={16} />}
          onClick={startEditing}
        >
          {t('Reply to this review')}
        </CardButton>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="subtitle2" gutterBottom>
            {isOwnReply ? t('Your reply') : t('Reply')}
          </Typography>
          {action}
        </Stack>
        <Box
          component={Card}
          variant="outlined"
          sx={{
            p: 2,
          }}
        >
          {isEditing ? editorView : readView}
        </Box>
      </Box>

      <SlideDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title={t('Delete reply?')}
        onSave={handleDelete}
        saveText={t('Delete')}
        saveColor="error"
        saveIcon={<Trash2 size={16} />}
        cancelText={t('Cancel')}
        loading={saving}
        maxWidth="xs"
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">
            {t(
              'Are you sure you want to delete your reply? This cannot be undone.',
            )}
          </Typography>
        </Box>
      </SlideDialog>

      <MutationErrorModal
        error={mutationError}
        onClose={() => setMutationError(null)}
      />
    </>
  );
};
